import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Box, Button, Center, Container, Divider, Group,
  Loader, Modal, NumberInput, Paper, ScrollArea, Select, SimpleGrid,
  Stack, Table, Tabs, Text, TextInput, ThemeIcon, Title, Tooltip,
  Notification,
} from '@mantine/core';
import {
  IconAlertTriangle, IconArrowLeft, IconCalendar, IconCar,
  IconChartBar, IconCheck, IconClock, IconDownload, IconEdit,
  IconFileInvoice, IconFilterOff, IconReceipt2, IconRefresh,
  IconSearch, IconTrash, IconUsers, IconWallet, IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';

// axiosConfig dosyasi zaten /api prefix'ini yonettigi icin URL'ler degistirilmedi.
const API_ROOT = '/istanbul-office';
const REPORTS = {
  vehicle: { endpoint: 'vehicles', file: 'istanbul_ofis_arac_raporu.xlsx', label: 'Araç Raporları' },
  guest: { endpoint: 'guests', file: 'istanbul_ofis_misafir_raporu.xlsx', label: 'Misafir Raporları' },
  expense: { endpoint: 'expenses', file: 'istanbul_ofis_gider_raporu.xlsx', label: 'Gider Raporları' },
};

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});
const moneyFormatter = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const formatCurrency = (amount) => moneyFormatter.format(Number(amount) || 0);

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : dateFormatter.format(parsed);
}

function formatDateOnly(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : '—';
}

function duration(start, end, ongoingLabel) {
  if (!start) return '—';
  if (!end) return ongoingLabel;
  const delta = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(delta) || delta < 0) return '—';
  const minutes = Math.floor(delta / 60000);
  return `${Math.floor(minutes / 60)} sa ${String(minutes % 60).padStart(2, '0')} dk`;
}

async function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const message = JSON.parse(await data.text());
      if (typeof message.detail === 'string') return message.detail;
      if (Array.isArray(message.detail)) return message.detail.map((d) => d.msg || String(d)).join(', ');
    } catch { /* Sunucu JSON disinda bir hata dondurmus olabilir. */ }
  }
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) return data.detail.map((d) => d.msg || String(d)).join(', ');
  if (!error?.response && error?.message) return `${fallback} (${error.message})`;
  return fallback;
}

function filenameFromHeader(header) {
  if (!header) return null;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try { return decodeURIComponent(utf8[1].replace(/["']/g, '')); } catch { /* fallback */ }
  }
  const basic = /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i.exec(header);
  return basic ? (basic[1] || basic[2]).trim() : null;
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Bazi tarayicilar indirmeyi baslatmadan revoke edilirse dosya bozulabilir.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

function MetricCard({ icon: Icon, title, value, note, color = 'blue' }) {
  return (
    <Paper radius="lg" p="lg" withBorder style={{ background: '#fff', borderColor: '#e6eaf0' }}>
      <Group align="flex-start" justify="space-between" wrap="nowrap">
        <div>
          <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: '.055em' }}>{title}</Text>
          <Title order={3} mt={6} fw={800} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Title>
          {note && <Text c="dimmed" size="xs" mt={4}>{note}</Text>}
        </div>
        <ThemeIcon color={color} variant="light" radius="md" size={46}>
          <Icon size={23} stroke={1.8} />
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

function EmptyState({ icon: Icon, message, description }) {
  return (
    <Center py={76}>
      <Stack align="center" gap="xs">
        <ThemeIcon color="gray" variant="light" radius="xl" size={62}><Icon size={28} /></ThemeIcon>
        <Text fw={700}>{message}</Text>
        <Text size="sm" c="dimmed" ta="center">{description}</Text>
      </Stack>
    </Center>
  );
}

const tableStyles = {
  th: { whiteSpace: 'nowrap', fontSize: 12, color: '#526179', background: '#f5f8fc', fontWeight: 700 },
  td: { fontSize: 13, verticalAlign: 'middle' },
};

export default function Reports() {
  const navigate = useNavigate();
  // Mevcut kodda admin bayragi true olarak tanimliydi; davranisi koruyoruz.
  // Gercek yetkilendirme mutlaka backend'de de uygulanmalidir.
  const isAdmin = true;

  const [activeTab, setActiveTab] = useState('vehicle');
  const [vehicleLogs, setVehicleLogs] = useState([]);
  const [guestLogs, setGuestLogs] = useState([]);
  const [expenseLogs, setExpenseLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ type: '', message: '' });
  const [expenseFilters, setExpenseFilters] = useState({
    search: '', category: '', createdBy: '', startDate: '', endDate: '',
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState({
    id: null, expense_date: '', expense_time: '', category: '',
    amount: '', description: '', created_by: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [expenseDeleteConfirm, setExpenseDeleteConfirm] = useState({ show: false, id: null });
  const [deletingExpense, setDeletingExpense] = useState(false);

  function alert(type, message) {
    setAlertInfo({ type, message });
  }

  async function fetchReport(type) {
    setLoading(true);
    try {
      const { data } = await api.get(`${API_ROOT}/reports/${REPORTS[type].endpoint}`);
      const logs = Array.isArray(data) ? data : [];
      if (type === 'vehicle') setVehicleLogs(logs);
      if (type === 'guest') setGuestLogs(logs);
      if (type === 'expense') setExpenseLogs(logs);
    } catch (error) {
      alert('error', await getErrorMessage(error, `${REPORTS[type].label} yüklenemedi.`));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport(activeTab);
    // Sekme degistiginde veriyi yenile; diger sekmelerin verilerine dokunma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const expenseCategories = useMemo(
    () => [...new Set(expenseLogs.map((item) => item.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')),
    [expenseLogs]
  );
  const expenseCreatedByOptions = useMemo(
    () => [...new Set(expenseLogs.map((item) => item.created_by).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')),
    [expenseLogs]
  );

  const filteredExpenses = useMemo(() => expenseLogs.filter((item) => {
    const term = expenseFilters.search.trim().toLocaleLowerCase('tr-TR');
    const date = String(item.expense_date || '').slice(0, 10);
    return (!term || [item.category, item.description, item.created_by, item.amount]
      .some((part) => String(part ?? '').toLocaleLowerCase('tr-TR').includes(term)))
      && (!expenseFilters.category || item.category === expenseFilters.category)
      && (!expenseFilters.createdBy || item.created_by === expenseFilters.createdBy)
      && (!expenseFilters.startDate || date >= expenseFilters.startDate)
      && (!expenseFilters.endDate || date <= expenseFilters.endDate);
  }), [expenseLogs, expenseFilters]);

  const expenseTotal = useMemo(
    () => filteredExpenses.reduce((total, item) => total + (Number(item.amount) || 0), 0),
    [filteredExpenses]
  );
  const activeVehicleCount = vehicleLogs.filter((log) => !log.return_time).length;
  const insideGuestCount = guestLogs.filter((log) => !log.exit_time).length;
  const hasExpenseFilter = Object.values(expenseFilters).some(Boolean);

  async function downloadExcel(type) {
    if (downloading) return;
    setDownloading(type);
    try {
      const params = type === 'expense' ? {
        search: expenseFilters.search || undefined,
        category: expenseFilters.category || undefined,
        created_by: expenseFilters.createdBy || undefined,
        start_date: expenseFilters.startDate || undefined,
        end_date: expenseFilters.endDate || undefined,
      } : undefined;
      const response = await api.get(`${API_ROOT}/reports/${REPORTS[type].endpoint}/export`, {
        params,
        responseType: 'blob',
        headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const signature = await blob.slice(0, 2).text();
      if (signature !== 'PK') {
        let detail = 'Sunucu geçerli bir Excel dosyası döndürmedi. Backend endpoint ve loglarını kontrol edin.';
        try {
          const body = JSON.parse(await blob.text());
          if (typeof body.detail === 'string') detail = body.detail;
        } catch { /* HTML hata sayfasi gelmis olabilir. */ }
        throw new Error(detail);
      }
      const fileName = filenameFromHeader(response.headers?.['content-disposition']) || REPORTS[type].file;
      saveBlob(blob, fileName);
      alert('success', 'Excel raporu başarıyla indirildi.');
    } catch (error) {
      console.error('Excel indirme hatası:', error);
      alert('error', error?.response
        ? await getErrorMessage(error, 'Excel raporu indirilemedi.')
        : (error?.message || 'Excel raporu indirilemedi.'));
    } finally {
      setDownloading(null);
    }
  }

  async function downloadInvoice(expense) {
    try {
      const response = await api.get(`${API_ROOT}/reports/expenses/${expense.id}/download-invoice`, {
        responseType: 'blob',
      });
      const fallback = expense.invoice_file_url?.split('/').pop() || `fatura_${expense.id}`;
      const filename = filenameFromHeader(response.headers?.['content-disposition']) || fallback;
      saveBlob(response.data, filename);
    } catch (error) {
      alert('error', await getErrorMessage(error, 'Fatura dosyası indirilemedi.'));
    }
  }

  function openExpenseEdit(expense) {
    setEditingExpense({
      id: expense.id,
      expense_date: String(expense.expense_date || '').slice(0, 10),
      expense_time: String(expense.expense_time || '').slice(0, 5),
      category: expense.category || '',
      amount: Number(expense.amount) || 0,
      description: expense.description || '',
      created_by: expense.created_by || '',
    });
    setEditOpen(true);
  }

  async function updateExpense(event) {
    event.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`${API_ROOT}/expenses/${editingExpense.id}`, {
        expense_date: editingExpense.expense_date,
        expense_time: editingExpense.expense_time,
        category: editingExpense.category,
        amount: Number(editingExpense.amount),
        description: editingExpense.description,
        created_by: editingExpense.created_by,
      });
      setEditOpen(false);
      alert('success', 'Gider kaydı güncellendi.');
      await fetchReport('expense');
    } catch (error) {
      alert('error', await getErrorMessage(error, 'Gider güncellenemedi.'));
    } finally {
      setSavingEdit(false);
    }
  }

  async function deleteExpense() {
    if (!expenseDeleteConfirm.id) return;
    setDeletingExpense(true);
    try {
      await api.delete(`${API_ROOT}/expenses/${expenseDeleteConfirm.id}`);
      setExpenseDeleteConfirm({ show: false, id: null });
      alert('success', 'Gider kaydı silindi.');
      await fetchReport('expense');
    } catch (error) {
      alert('error', await getErrorMessage(error, 'Gider silinemedi.'));
    } finally {
      setDeletingExpense(false);
    }
  }

  const downloadButton = (type) => (
    <Button
      color="teal"
      radius="md"
      leftSection={<IconDownload size={18} />}
      loading={downloading === type}
      disabled={Boolean(downloading) && downloading !== type}
      onClick={() => downloadExcel(type)}
    >
      Excel İndir
    </Button>
  );

  const sectionHeading = (title, sub, type) => (
    <Group justify="space-between" align="center" mb="lg" gap="md">
      <div>
        <Title order={3} fw={800} size="h4">{title}</Title>
        <Text size="sm" c="dimmed" mt={4}>{sub}</Text>
      </div>
      {downloadButton(type)}
    </Group>
  );

  return (
    <Box bg="#f4f7fb" mih="100vh" py={{ base: 'md', md: 'xl' }}>
      <Container size="xl">
        <Paper
          radius="xl" p={{ base: 'lg', sm: 'xl' }} mb="lg"
          style={{
            background: 'linear-gradient(110deg, #10243e 0%, #164578 70%, #1c66a9 100%)',
            color: 'white', boxShadow: '0 16px 32px rgba(20,51,82,.15)',
          }}
        >
          <Group justify="space-between" align="center" gap="lg">
            <Group gap="md" align="center">
              <ThemeIcon size={56} radius="lg" color="white" variant="light">
                <IconChartBar size={29} color="white" stroke={1.7} />
              </ThemeIcon>
              <div>
                <Text size="xs" tt="uppercase" fw={700} style={{ letterSpacing: '.11em', color: '#c3dafa' }}>
                  SMS GRUP / İSTANBUL OFİS
                </Text>
                <Title order={2} c="white" fw={800} mt={3}>Operasyon Raporları</Title>
                <Text size="sm" mt={5} style={{ color: '#dce9f8' }}>
                  Araç hareketleri, ziyaretçiler ve ofis giderleri tek merkezde.
                </Text>
              </div>
            </Group>
            <Group gap="sm">
              <Button
                variant="white" color="dark" radius="md"
                leftSection={<IconArrowLeft size={17} />}
                onClick={() => navigate('/istanbul-office/dashboard')}
              >
                Panele Dön
              </Button>
              <Button
                radius="md" variant="outline" c="white"
                style={{ borderColor: 'rgba(255,255,255,.55)' }}
                leftSection={<IconRefresh size={17} />}
                loading={loading} onClick={() => fetchReport(activeTab)}
              >
                Yenile
              </Button>
            </Group>
          </Group>
        </Paper>

        {alertInfo.message && (
          <Notification
            mb="lg" radius="lg" withBorder
            color={alertInfo.type === 'success' ? 'teal' : 'red'}
            icon={alertInfo.type === 'success' ? <IconCheck size={18} /> : <IconX size={18} />}
            onClose={() => setAlertInfo({ type: '', message: '' })}
          >
            {alertInfo.message}
          </Notification>
        )}

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
          {activeTab === 'vehicle' ? (
            <>
              <MetricCard icon={IconCar} title="Toplam Araç Hareketi" value={vehicleLogs.length} note="Kayıtlı görevler" />
              <MetricCard icon={IconClock} title="Devam Eden Görev" value={activeVehicleCount} note="Dönüşü gerçekleşmeyen" color="orange" />
              <MetricCard icon={IconCheck} title="Tamamlanan Görev" value={vehicleLogs.length - activeVehicleCount} color="teal" />
            </>
          ) : activeTab === 'guest' ? (
            <>
              <MetricCard icon={IconUsers} title="Toplam Ziyaret" value={guestLogs.length} note="Geçmiş misafir kayıtları" />
              <MetricCard icon={IconClock} title="İçerideki Misafir" value={insideGuestCount} color="orange" />
              <MetricCard icon={IconCheck} title="Çıkış Yapan" value={guestLogs.length - insideGuestCount} color="teal" />
            </>
          ) : (
            <>
              <MetricCard icon={IconReceipt2} title="Gösterilen Gider" value={filteredExpenses.length} note={`${expenseLogs.length} kayıt içinden`} />
              <MetricCard icon={IconWallet} title="Filtrelenen Toplam" value={formatCurrency(expenseTotal)} color="red" />
              <MetricCard icon={IconFileInvoice} title="Faturalı Kayıt" value={filteredExpenses.filter((e) => e.invoice_file_url).length} color="teal" />
            </>
          )}
        </SimpleGrid>

        <Paper radius="xl" withBorder p={{ base: 'md', sm: 'xl' }} style={{ borderColor: '#e5eaf1', boxShadow: '0 10px 25px rgba(20,51,82,.04)' }}>
          <Tabs value={activeTab} onChange={setActiveTab} color="blue" radius="md" variant="pills">
            <ScrollArea type="auto" mb="xl" offsetScrollbars>
              <Tabs.List style={{ flexWrap: 'nowrap', minWidth: 'max-content', background: '#f0f4f9', padding: 5, borderRadius: 10 }}>
                <Tabs.Tab value="vehicle" leftSection={<IconCar size={17} />}>Araç Raporları</Tabs.Tab>
                <Tabs.Tab value="guest" leftSection={<IconUsers size={17} />}>Misafir Raporları</Tabs.Tab>
                <Tabs.Tab value="expense" leftSection={<IconReceipt2 size={17} />}>Gider Raporları</Tabs.Tab>
              </Tabs.List>
            </ScrollArea>

            <Tabs.Panel value="vehicle">
              {sectionHeading('Araç Hareketleri', 'Tüm görev, çıkış ve dönüş hareketleri.', 'vehicle')}
              {loading ? <Center py={80}><Loader /></Center> : vehicleLogs.length === 0 ? (
                <EmptyState icon={IconCar} message="Araç hareketi bulunamadı." description="Yeni görevler eklendiğinde burada görüntülenir." />
              ) : (
                <ScrollArea type="auto" offsetScrollbars>
                  <Table striped highlightOnHover verticalSpacing="md" horizontalSpacing="md" miw={1020}>
                    <Table.Thead><Table.Tr>
                      {['Plaka', 'Araç Modeli', 'Sürücü', 'Görev Nedeni', 'Çıkış', 'Dönüş', 'Süre', 'Durum'].map((head) => (
                        <Table.Th key={head} style={tableStyles.th}>{head}</Table.Th>
                      ))}
                    </Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {vehicleLogs.map((log) => (
                        <Table.Tr key={log.id}>
                          <Table.Td style={tableStyles.td}><Text fw={700} size="sm">{log.vehicle?.plate || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}>{log.vehicle?.model || '—'}</Table.Td>
                          <Table.Td style={tableStyles.td}>{log.driver_name || '—'}</Table.Td>
                          <Table.Td style={tableStyles.td}><Text lineClamp={2} size="sm">{log.reason || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}>{formatDateTime(log.out_time)}</Table.Td>
                          <Table.Td style={tableStyles.td}>{formatDateTime(log.return_time)}</Table.Td>
                          <Table.Td style={tableStyles.td}>{duration(log.out_time, log.return_time, 'Görevde')}</Table.Td>
                          <Table.Td style={tableStyles.td}>
                            <Badge color={log.return_time ? 'teal' : 'orange'} variant="light" radius="sm">
                              {log.return_time ? 'Tamamlandı' : 'Görevde'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="guest">
              {sectionHeading('Ziyaretçi Hareketleri', 'Giriş, çıkış ve ziyaret süresi raporları.', 'guest')}
              {loading ? <Center py={80}><Loader /></Center> : guestLogs.length === 0 ? (
                <EmptyState icon={IconUsers} message="Misafir kaydı bulunamadı." description="Ziyaretçi kayıtları burada görüntülenir." />
              ) : (
                <ScrollArea type="auto" offsetScrollbars>
                  <Table striped highlightOnHover verticalSpacing="md" horizontalSpacing="md" miw={960}>
                    <Table.Thead><Table.Tr>
                      {['Misafir Adı', 'Ziyaret Edilen', 'Sebep / Firma', 'Giriş', 'Çıkış', 'Süre', 'Durum'].map((head) => (
                        <Table.Th key={head} style={tableStyles.th}>{head}</Table.Th>
                      ))}
                    </Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {guestLogs.map((log) => (
                        <Table.Tr key={log.id}>
                          <Table.Td style={tableStyles.td}><Text fw={700} size="sm">{log.visitor_name || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}>{log.host_name || '—'}</Table.Td>
                          <Table.Td style={tableStyles.td}><Text lineClamp={2} size="sm">{log.reason || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}>{formatDateTime(log.entry_time)}</Table.Td>
                          <Table.Td style={tableStyles.td}>{formatDateTime(log.exit_time)}</Table.Td>
                          <Table.Td style={tableStyles.td}>{duration(log.entry_time, log.exit_time, 'İçeride')}</Table.Td>
                          <Table.Td style={tableStyles.td}>
                            <Badge color={log.exit_time ? 'teal' : 'blue'} variant="light" radius="sm">
                              {log.exit_time ? 'Ayrıldı' : 'İçeride'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="expense">
              {sectionHeading('Gider Hareketleri', 'Kategori, tarih ve kullanıcıya göre filtrelenebilir gider raporu.', 'expense')}
              <Paper withBorder radius="lg" p="md" bg="#f9fbfe" mb="lg" style={{ borderColor: '#e7edf5' }}>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs"><IconSearch size={17} color="#46668b" /><Text size="sm" fw={700}>Rapor Filtreleri</Text></Group>
                  {hasExpenseFilter && <Badge color="blue" variant="light">Filtre Aktif</Badge>}
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                  <TextInput
                    label="Arama" placeholder="Kategori, açıklama, tutar, kişi..."
                    leftSection={<IconSearch size={16} />}
                    value={expenseFilters.search}
                    onChange={(e) => setExpenseFilters((old) => ({ ...old, search: e.currentTarget.value }))}
                  />
                  <Select
                    label="Kategori" placeholder="Tüm Kategoriler" data={expenseCategories}
                    value={expenseFilters.category || null}
                    onChange={(value) => setExpenseFilters((old) => ({ ...old, category: value || '' }))}
                    searchable clearable
                  />
                  <Select
                    label="İşleyen Kişi" placeholder="Tüm Kullanıcılar" data={expenseCreatedByOptions}
                    value={expenseFilters.createdBy || null}
                    onChange={(value) => setExpenseFilters((old) => ({ ...old, createdBy: value || '' }))}
                    searchable clearable
                  />
                  <TextInput
                    label="Başlangıç Tarihi" type="date"
                    leftSection={<IconCalendar size={16} />}
                    value={expenseFilters.startDate}
                    max={expenseFilters.endDate || undefined}
                    onChange={(e) => setExpenseFilters((old) => ({ ...old, startDate: e.currentTarget.value }))}
                  />
                  <TextInput
                    label="Bitiş Tarihi" type="date"
                    leftSection={<IconCalendar size={16} />}
                    min={expenseFilters.startDate || undefined}
                    value={expenseFilters.endDate}
                    onChange={(e) => setExpenseFilters((old) => ({ ...old, endDate: e.currentTarget.value }))}
                  />
                  <Group align="flex-end" pb={1}>
                    <Button
                      variant="light" color="gray" radius="md" fullWidth
                      leftSection={<IconFilterOff size={17} />}
                      disabled={!hasExpenseFilter}
                      onClick={() => setExpenseFilters({ search: '', category: '', createdBy: '', startDate: '', endDate: '' })}
                    >Filtreleri Temizle</Button>
                  </Group>
                </SimpleGrid>
              </Paper>

              <Group justify="space-between" mb="sm">
                <Text size="sm" c="dimmed">{filteredExpenses.length} / {expenseLogs.length} kayıt gösteriliyor</Text>
                {hasExpenseFilter && <Text size="xs" c="blue">Excel, ekrandaki filtreleri dikkate alır.</Text>}
              </Group>

              {loading ? <Center py={80}><Loader /></Center> : filteredExpenses.length === 0 ? (
                <EmptyState
                  icon={IconReceipt2}
                  message={expenseLogs.length === 0 ? 'Gider kaydı bulunamadı.' : 'Filtrelerle eşleşen kayıt bulunamadı.'}
                  description="Filtreleri değiştirerek tekrar deneyebilirsiniz."
                />
              ) : (
                <ScrollArea type="auto" offsetScrollbars>
                  <Table striped highlightOnHover verticalSpacing="md" horizontalSpacing="md" miw={1020}>
                    <Table.Thead><Table.Tr>
                      {['Tarih', 'Saat', 'Kategori', 'Tutar', 'Açıklama', 'İşleyen Kişi', 'İşlemler / Fatura'].map((head) => (
                        <Table.Th key={head} style={tableStyles.th}>{head}</Table.Th>
                      ))}
                    </Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {filteredExpenses.map((log) => (
                        <Table.Tr key={log.id}>
                          <Table.Td style={tableStyles.td}>{formatDateOnly(log.expense_date)}</Table.Td>
                          <Table.Td style={tableStyles.td}>{String(log.expense_time || '').slice(0, 5)}</Table.Td>
                          <Table.Td style={tableStyles.td}><Text fw={700} size="sm">{log.category || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}><Text fw={800} c="red.8" size="sm" style={{ whiteSpace: 'nowrap' }}>{formatCurrency(log.amount)}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}><Text lineClamp={2} size="sm">{log.description || '—'}</Text></Table.Td>
                          <Table.Td style={tableStyles.td}>{log.created_by || '—'}</Table.Td>
                          <Table.Td style={tableStyles.td}>
                            <Group gap={6} wrap="nowrap">
                              {log.invoice_file_url ? (
                                <Tooltip label="Faturayı indir" withArrow>
                                  <ActionIcon variant="light" color="blue" radius="md" size="lg" aria-label="Faturayı indir" onClick={() => downloadInvoice(log)}>
                                    <IconFileInvoice size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              ) : <Text size="xs" c="dimmed">Belge yok</Text>}
                              {isAdmin && (
                                <>
                                  <Tooltip label="Gideri düzenle" withArrow>
                                    <ActionIcon variant="light" color="orange" radius="md" size="lg" aria-label="Gideri düzenle" onClick={() => openExpenseEdit(log)}>
                                      <IconEdit size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Gideri sil" withArrow>
                                    <ActionIcon variant="light" color="red" radius="md" size="lg" aria-label="Gideri sil" onClick={() => setExpenseDeleteConfirm({ show: true, id: log.id })}>
                                      <IconTrash size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                    <Table.Tfoot>
                      <Table.Tr style={{ background: '#edf4fb' }}>
                        <Table.Td colSpan={3}><Text fw={800} ta="right" size="sm">FİLTRELENEN TOPLAM</Text></Table.Td>
                        <Table.Td><Text fw={900} c="red.8" style={{ whiteSpace: 'nowrap' }}>{formatCurrency(expenseTotal)}</Text></Table.Td>
                        <Table.Td colSpan={3} />
                      </Table.Tr>
                    </Table.Tfoot>
                  </Table>
                </ScrollArea>
              )}
            </Tabs.Panel>
          </Tabs>
          <Divider mt="xl" color="#e9edf4" />
          <Text mt="md" size="xs" c="dimmed">
            Raporlar sistemdeki güncel kayıtları esas alır. İndirilen Excel dosyaları analiz ve arşivleme amacıyla düzenlenmiştir.
          </Text>
        </Paper>
      </Container>

      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={<Text fw={800}>Gider Bilgilerini Düzenle</Text>} centered radius="lg" size="lg">
        <form onSubmit={updateExpense}>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Tarih" type="date" value={editingExpense.expense_date} required
                onChange={(e) => setEditingExpense((old) => ({ ...old, expense_date: e.currentTarget.value }))} />
              <TextInput label="Saat" type="time" value={editingExpense.expense_time} required
                onChange={(e) => setEditingExpense((old) => ({ ...old, expense_time: e.currentTarget.value }))} />
            </SimpleGrid>
            <Select label="Kategori" required searchable
              data={[...new Set(['Akaryakıt', 'Kırtasiye', 'Yemek', 'Konaklama', 'Hırdavat/Malzeme', 'Diğer', editingExpense.category].filter(Boolean))]}
              value={editingExpense.category}
              onChange={(value) => setEditingExpense((old) => ({ ...old, category: value || '' }))} />
            <NumberInput label="Tutar (TL)" required decimalScale={2} fixedDecimalScale hideControls min={0}
              value={editingExpense.amount} onChange={(value) => setEditingExpense((old) => ({ ...old, amount: value }))} />
            <TextInput label="Açıklama" value={editingExpense.description}
              onChange={(e) => setEditingExpense((old) => ({ ...old, description: e.currentTarget.value }))} />
            <TextInput label="İşleyen Kişi" required value={editingExpense.created_by}
              onChange={(e) => setEditingExpense((old) => ({ ...old, created_by: e.currentTarget.value }))} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setEditOpen(false)}>İptal</Button>
              <Button type="submit" color="blue" loading={savingEdit}>Değişiklikleri Kaydet</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={expenseDeleteConfirm.show} onClose={() => setExpenseDeleteConfirm({ show: false, id: null })}
        title={<Text fw={800}>Gider Kaydını Sil</Text>} centered radius="lg" size="md">
        <Stack align="center" gap="md">
          <ThemeIcon radius="xl" variant="light" color="red" size={64}><IconAlertTriangle size={31} /></ThemeIcon>
          <Text size="sm" c="dimmed" ta="center">
            Bu gider kaydı ve varsa ilgili fatura dosyası kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </Text>
          <Group justify="center" w="100%">
            <Button variant="default" onClick={() => setExpenseDeleteConfirm({ show: false, id: null })}>Vazgeç</Button>
            <Button color="red" loading={deletingExpense} onClick={deleteExpense}>Evet, Sil</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
